import { MetadataStore } from './MetadataStore';
import { ResolverApis } from './types';
import { CompRef } from '@wix/document-services-types';
import {
  IVirtualManifestBuilderProps,
  VirtualElement,
  VirtualElementAPIType,
} from '@wix/editor-platform-manifest-services';
import { EditorPointer } from '@wix/editor-document-logic-api';

type Node = VirtualElement<VirtualElementAPIType.VirtualManifestAPI>;

type BuildManifestProps = Pick<
  IVirtualManifestBuilderProps,
  'editorPointers' | 'contextBuilder'
>;

export class DisplayElementsPanelVisibilityToggler {
  constructor(
    private readonly apis: ResolverApis,
    private readonly metadataStore: MetadataStore,
  ) {}

  #getNodeById(id: string, buildManifestProps: BuildManifestProps) {
    const metadata = this.metadataStore.getItem(id);

    if (!metadata) {
      return;
    }

    const { editorPointer } = metadata;

    const manifestAPI =
      this.apis.componentsAPI.buildManifestAPI(buildManifestProps);

    const childrenNodes = manifestAPI.node.getChildren({ recursive: true });

    const nodes = [
      ...(manifestAPI.node.isRoot() ? [] : [manifestAPI.node]),
      ...childrenNodes,
    ];

    return nodes.find((node) =>
      this.apis.editorPointerAPI.isSameEditorPointer(
        node.getEditorPointer(),
        editorPointer,
      ),
    );
  }

  #getDirectChildren(node: Node) {
    return this.metadataStore.filterNodesWithExistingEditorPointers(
      node.getChildren(),
    );
  }

  #getDescendants(node: Node) {
    return this.metadataStore.filterNodesWithExistingEditorPointers(
      node.getChildren({ recursive: true }),
    );
  }

  #getParents(node: Node) {
    return this.metadataStore.filterNodesWithExistingEditorPointers(
      node.getParents(),
    );
  }

  #isNodeVisible(node: Node, data: Record<string, boolean>): boolean {
    return this.metadataStore
      .getAllItemsByEditorPointer(node.getEditorPointer())
      .some(([id]) => data[id]);
  }

  #isNodeFullyDisabled(node: Node, data: Record<string, boolean>): boolean {
    return this.metadataStore
      .getAllItemsByEditorPointer(node.getEditorPointer())
      .every(([id]) => !data[id]);
  }

  #isClickedOnIndeterminateContainer(
    node: Node,
    data: Record<string, boolean>,
  ) {
    const childrenNodes = this.#getDirectChildren(node);

    if (childrenNodes.length === 1) {
      return !this.#isNodeVisible(childrenNodes[0], data);
    }

    const includesEnabled = childrenNodes.some((child) =>
      this.#isNodeVisible(child, data),
    );

    const includesDisabled = childrenNodes.some(
      (child) => !this.#isNodeVisible(child, data),
    );

    return includesEnabled && includesDisabled;
  }

  #toggle(
    enable: boolean,
    editorPointer: EditorPointer<CompRef>,
    key: string | undefined,
    preservedVisibleStyleValue: string | undefined,
  ) {
    if (enable) {
      this.apis.componentsAPI.displayVisibility.showElementOnStage(
        editorPointer,
        key,
        preservedVisibleStyleValue,
      );

      return;
    }

    this.apis.componentsAPI.displayVisibility.hideElementOnStage(
      editorPointer,
      key,
    );
  }

  #toggleSpecificEntry(id: string, enable: boolean): Record<string, boolean> {
    const value = this.metadataStore.getItem(id);
    const key =
      value.namespace === 'cssCustomProperties' ? value.key : undefined;

    this.#toggle(
      enable,
      value.visibilityEditorPointer,
      key,
      value.preservedVisibleStyleValue,
    );

    return { [id]: enable };
  }

  #processToggleCascade(
    nodes: Node[],
    enable: boolean,
  ): Record<string, boolean> {
    return nodes.reduce<Record<string, boolean>>((acc, node) => {
      const entries = this.metadataStore.getAllItemsByEditorPointer(
        node.getEditorPointer(),
      );

      entries.forEach(([entryId, value]) => {
        const key =
          value.namespace === 'cssCustomProperties' ? value.key : undefined;

        this.#toggle(
          enable,
          value.visibilityEditorPointer,
          key,
          value.preservedVisibleStyleValue,
        );

        acc[entryId] = enable;
      });

      return acc;
    }, {});
  }

  #showElements(id: string, node: Node) {
    const parentNodes = this.#getParents(node);
    const descendants = this.#getDescendants(node);

    return {
      ...this.#toggleSpecificEntry(id, true),
      ...this.#processToggleCascade(parentNodes, true),
      ...this.#processToggleCascade(descendants, true),
    };
  }

  #findParentContainersToDisable(
    clickedNode: Node,
    data: Record<string, boolean>,
  ): Node[] {
    const collectParents = (node: Node, acc: Node[] = []): Node[] => {
      const parent = node.getParent();

      if (!parent || parent.isRoot()) {
        return acc;
      }

      const siblings = this.#getDirectChildren(parent);

      const isAllSiblingsDisabled = siblings.every((sibling) =>
        this.#isNodeFullyDisabled(sibling, data),
      );

      if (!isAllSiblingsDisabled) {
        return acc;
      }

      const isValidParent =
        this.metadataStore.filterNodesWithExistingEditorPointers([parent])[0];

      if (!isValidParent) {
        return acc;
      }

      return collectParents(parent, [...acc, parent]);
    };

    return collectParents(clickedNode);
  }

  #hideElements(id: string, node: Node, data: Record<string, boolean>) {
    const nextData = { ...data, [id]: false };
    const descendants = this.#getDescendants(node);
    const disabledParentContainers = this.#findParentContainersToDisable(
      node,
      nextData,
    );

    return {
      ...this.#toggleSpecificEntry(id, false),
      ...this.#processToggleCascade(descendants, false),
      ...this.#processToggleCascade(disabledParentContainers, false),
    };
  }

  onToggleState(
    id: string,
    enable: boolean,
    respectIndeterminate: boolean,
    buildManifestProps: BuildManifestProps,
    elementsVisibilityData: Record<string, boolean>,
  ) {
    const clickedNode = this.#getNodeById(id, buildManifestProps);

    if (!clickedNode) {
      return {};
    }

    if (
      !enable &&
      this.#isClickedOnIndeterminateContainer(
        clickedNode,
        elementsVisibilityData,
      ) &&
      respectIndeterminate
    ) {
      return this.#showElements(id, clickedNode);
    }

    return enable
      ? this.#showElements(id, clickedNode)
      : this.#hideElements(id, clickedNode, elementsVisibilityData);
  }
}
